import React, { createContext, useContext, useEffect, useState } from "react";
import map from "lodash/map";
import remove from "lodash/remove";
import findIndex from "lodash/findIndex";
import get from "lodash/get";
import set from "lodash/set";
import find from "lodash/find";
import keys from "lodash/keys";
import reduce from "lodash/reduce";
import size from "lodash/size";
import DocumentsService from "../../services/DocumentsService";
import {
  extractInformationFromFile,
  getDefaultOffices,
} from "../../helpers/utils";
import useOffices from "../hooks/useOffices";
import EstateService from "../../services/EstateService";

const FileContext = createContext({});

const FileWrapper = ({ children, estateId }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filesAssociation, setFilesAssociation] = useState({});
  const [estatesSuggestions, setEstatesSuggestions] = useState([]);
  const [displayMsg, setDisplayMsg] = useState(false);
  const [isError, setIsError] = useState(false);

  const { data: offices } = useOffices();

  const getMathchingEstates = async (files) => {
    try {
      const defautOffices = map(
        getDefaultOffices(offices),
        (office) => office.id
      );
      const filenames = map(files, (f) => f.name);
      const data = await EstateService.getEstatesSuggestions({
        filenames,
        officeIds: defautOffices,
        purposeIds: [1],
        purposeStatusIds: [1, 5],
        displayStatusIds: [2, 3],
      });
      setEstatesSuggestions([...estatesSuggestions, ...get(data, "data", [])]);
    } catch (error) {
      console.error(error);
    }
  };

  const onDrop = (droppedFiles) => {
    const filesWithUuid = map(droppedFiles, (file) => ({
      uuid: crypto.randomUUID(),
      content: file,
      ...extractInformationFromFile(file),
    }));
    !estateId && getMathchingEstates(filesWithUuid);
    setSelectedFiles([...selectedFiles, ...filesWithUuid]);
  };

  const onRemove = (uuid) => {
    const arr = [...selectedFiles];
    remove(arr, (file) => file.uuid === uuid);
    setSelectedFiles(arr);
  };

  const updateFileInformation = (uuid, data) => {
    const fileIndex = findIndex(selectedFiles, (f) => f.uuid === uuid);
    const file = get(selectedFiles, fileIndex);
    const {
      name,
      effectiveDate,
      effectiveDateStr,
      documentType,
      documentTypeName,
    } = data;
    const updatedFile = {
      ...file,
      name,
      effectiveDate,
      effectiveDateStr,
      documentType,
      documentTypeName,
    };
    const tempFiles = [...selectedFiles];
    set(tempFiles, fileIndex, updatedFile);
    setSelectedFiles(tempFiles);
  };

  const addAssociation = (uuid, estates) => {
    setFilesAssociation({
      ...filesAssociation,
      [uuid]: estates,
    });
  };

  const uploadFiles = async () => {
    try {
      setIsLoading(true);

      let filesAssociationValue;
      if (!estateId) {
        let isValid = true;
        for (const file of selectedFiles) {
          const uuid = file.uuid;
          const association = filesAssociation[uuid] || [];
          if (association.length === 0) {
            isValid = false;
            break;
          }
        }

        if (!isValid) {
          setIsError(true);
          setDisplayMsg(true);
          return;
        }

        filesAssociationValue = reduce(
          keys(filesAssociation),
          (acc, uuid) => {
            acc[uuid] = map(filesAssociation[uuid], (e) => e.id);
            return acc;
          },
          {}
        );
      } else {
        filesAssociationValue = reduce(
          map(selectedFiles, (f) => f.uuid),
          (acc, uuid) => {
            acc[uuid] = [estateId];
            return acc;
          },
          {}
        );
      }

      const res = await DocumentsService.uploadDocuments(
        selectedFiles,
        filesAssociationValue
      );
      if (res.data) {
        clearAllAssociation();
        setSelectedFiles([]);
      }

      setIsError(!res.data);
      setDisplayMsg(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearAllAssociation = () => setFilesAssociation({});
  const clearAll = () => {
    setFilesAssociation({});
    setSelectedFiles([]);
  };

  const closeMsg = () => setDisplayMsg(false);

  useEffect(() => {
    if (size(estatesSuggestions) > 0 && size(selectedFiles) > 0) {
      let values = {};
      for (const file of selectedFiles) {
        const { uuid, name } = file;
        const associatedEstates = get(filesAssociation, uuid, []);
        if (size(associatedEstates) === 0) {
          const suggestions = find(
            estatesSuggestions,
            (e) => e.filename === name
          );
          values[uuid] = get(suggestions, "estates", []);
        }
      }
      setFilesAssociation({ ...filesAssociation, ...values });
    }
  }, [selectedFiles, estatesSuggestions]);

  return (
    <FileContext.Provider
      value={{
        selectedFiles,
        filesAssociation,
        onDrop,
        onRemove,
        updateFileInformation,
        addAssociation,
        clearAllAssociation,
        clearAll,
        submit: uploadFiles,
        displayMsg,
        closeMsg,
        isError,
        isLoading,
      }}
    >
      {children}
    </FileContext.Provider>
  );
};

export const useFileContext = () => {
  const value = useContext(FileContext);
  return value;
};

export default FileWrapper;
